// The renewal drawer: a checklist you can actually tick, the key dates, and
// the market feedback that decides whether a programme renews.

import React, { useEffect, useState } from 'react';
import {
  C, Pill, Panel, Row, MiniTable, NextAction, money0, date, humanise,
} from './kit';
import {
  useAction, Notice, ActionButton, ActionRow, ConfirmAction, ToggleChecklist,
  Input, FieldRow,
  EditPanel, StatusAction, RemoveAction,
} from './actions';
import { DocumentsTab, ActivityTab } from './tabs';

export default function RenewalDrawer({ row: r, tab, reload }) {
  // The three nearest key dates still outstanding, soonest first, flagged
  // when the date has already passed.
  const nowMs = Date.now();
  const nextDates = [...(r.keyDates || [])]
    .filter(k => k.date && !k.done)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 3)
    .map(k => ({ ...k, overdue: new Date(k.date).getTime() < nowMs }));
  const act = useAction(reload);
  const [list, setList] = useState(r.checklist || []);
  const [fb, setFb] = useState({ market: '', response: 'indication', note: '' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setList(r.checklist || []); }, [r._id]);

  const done = list.filter(i => i.completed).length;

  // Ticking saves straight away. The progress figure is the point of the
  // panel, and a Save button between the click and the number makes it lie
  // until someone remembers to press it.
  const toggle = (i, next) => {
    const updated = list.map((item, idx) => (idx === i
      ? {
        ...item,
        completed: next,
        completedAt: next ? new Date().toISOString() : undefined,
        completedByName: next ? (r.handlerName || undefined) : undefined,
      }
      : item));
    setList(updated);
    return act.run({
      method: 'patch',
      url: `/reinsurance/renewals/${r._id}`,
      body: { checklist: updated },
      success: null,
    });
  };

  // ─── Timeline ───
  if (tab === 'Timeline') {
    const now = Date.now();
    const dates = [...(r.keyDates || [])].sort(
      (a, b) => new Date(a.date || 0) - new Date(b.date || 0),
    );
    return (
      <Panel title='Key dates'>
        {dates.length === 0 ? (
          <div style={{ fontSize: 12.5, color: C.faint }}>No milestones set for this renewal.</div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 2 }}>
            {dates.map((d, i) => {
              const past = d.date && new Date(d.date).getTime() < now;
              return (
                <li key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 2px',
                  borderBottom: i < dates.length - 1 ? `1px solid ${C.hair}` : 'none',
                }}>
                  <span style={{
                    width: 9, height: 9, borderRadius: 999, flexShrink: 0,
                    background: past ? C.green : C.border,
                  }} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: past ? C.body : C.ink }}>
                    {d.label}
                  </span>
                  <span style={{ fontSize: 12, color: past ? C.muted : C.ink, fontWeight: past ? 500 : 600 }}>
                    {date(d.date)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        <div style={{ fontSize: 11.5, color: C.faint, lineHeight: 1.5, marginTop: 11 }}>
          A filled dot is a date that has passed. Nothing here is inferred — these
          are the milestones set on the renewal plan.
        </div>
      </Panel>
    );
  }

  // ─── Markets ───
  if (tab === 'Markets') {
    return (
      <>
        <Notice error={act.error} note={act.note} />
        <Panel title='Current markets'>
          <MiniTable
            head={['Market', 'Response']}
            rows={(r.marketFeedback || []).length
              ? r.marketFeedback.map(f => [f.market || f.name, humanise(f.response || '—')])
              : (r.currentMarkets || []).map(m => [m, '—'])}
            empty='No markets recorded on this renewal.'
          />
        </Panel>

        <Panel title='Record market feedback'>
          <div style={{ display: 'grid', gap: 9 }}>
            <FieldRow>
              <Input
                label='Market'
                value={fb.market}
                onChange={(v) => setFb(p => ({ ...p, market: v }))}
                placeholder='Swiss Re'
              />
              <Input
                label='Response'
                value={fb.response}
                onChange={(v) => setFb(p => ({ ...p, response: v }))}
                options={[
                  { value: 'indication', label: 'Indication' },
                  { value: 'firm_quote', label: 'Firm quote' },
                  { value: 'declined', label: 'Declined' },
                  { value: 'pending', label: 'Pending' },
                ]}
              />
            </FieldRow>
            <Input
              label='Note'
              value={fb.note}
              onChange={(v) => setFb(p => ({ ...p, note: v }))}
              placeholder='Holding terms subject to updated exposure data'
            />
            <ActionRow>
              <ActionButton
                busy={act.saving}
                disabled={!fb.market.trim()}
                onClick={async () => {
                  const ok = await act.run({
                    url: `/reinsurance/renewals/${r._id}/feedback`,
                    // `name` and `notes` — the endpoint's allow-list, and the
                    // shape the record stores. Sent as `market`/`note` these
                    // were both dropped on the way in, leaving an empty body
                    // and the refusal "Feedback needs a market", on a form
                    // where the market had plainly been typed in.
                    body: { name: fb.market.trim(), response: fb.response, notes: fb.note.trim() },
                    success: 'Feedback recorded.',
                  });
                  if (ok) setFb({ market: '', response: 'indication', note: '' });
                }}
              >
                Add feedback
              </ActionButton>
            </ActionRow>
          </div>
        </Panel>
      </>
    );
  }

  // ─── Documents ───
  if (tab === 'Documents') return <DocumentsTab record={r} />;


  // ─── Overview ───
  return (
    <>
      <Notice error={act.error} note={act.note} />

      <Panel title='Key information'>
        <Row label='Cedant' value={r.cedantName} />
        <Row label='Class of business' value={r.classOfBusiness} />
        <Row label='Type' value={humanise(r.type)} />
        <Row label='Renewal date' value={date(r.renewalDate)} />
        <Row label='Current markets' value={(r.currentMarkets || []).join(', ')} />
        <Row label='Target strategy' value={r.targetStrategy} />
        <Row label='Assigned handler' value={r.handlerName} />
        <Row label='Expiring premium' value={money0(r.premium?.expiring, r.currency)} />
        <Row label='Proposed premium' value={money0(r.premium?.proposed, r.currency)} />
        {/* changePercent is computed on the server when the record saves —
            read, never re-derived here, or the two would disagree the moment
            an agreed premium replaces the proposed one. */}
        <Row
          label='Change'
          value={r.premium?.changePercent === null || r.premium?.changePercent === undefined
            ? null
            : `${r.premium.changePercent > 0 ? '+' : ''}${r.premium.changePercent}%`}
        />
        <Row label='Status' value={<Pill status={r.status} size='sm' />} />
      </Panel>

      <Panel title='Renewal checklist'>
        <ToggleChecklist
          items={list}
          onToggle={toggle}
          saving={act.saving}
          countLabel={`${done} of ${list.length} completed`}
        />
      </Panel>

      <Panel title='Progress'>
        <FieldRow>
          <Input
            label='Renewal status'
            value={r.status || 'on_track'}
            onChange={(v) => act.run({
              method: 'patch',
              url: `/reinsurance/renewals/${r._id}`,
              body: { status: v },
              success: `Moved to ${humanise(v)}.`,
            })}
            options={[
              { value: 'on_track', label: 'On Track' },
              { value: 'due_soon', label: 'Due Soon' },
              { value: 'at_risk', label: 'At Risk' },
              { value: 'overdue', label: 'Overdue' },
              { value: 'declined', label: 'Declined' },
            ]}
          />
        </FieldRow>
      </Panel>

      {/* ── Next key dates ──
          The dates the desk works BACK from. They were on the Timeline tab
          only, which meant the one question the Overview is opened to answer
          — what is next and have we missed it — needed a second click. The
          three nearest outstanding dates, overdue ones in red. */}
      <Panel title='Next key dates'>
        {nextDates.length === 0 ? (
          <div style={{ fontSize: 12.5, color: C.faint }}>
            No outstanding key dates on this renewal.
          </div>
        ) : (
          <MiniTable
            head={['Milestone', 'Date']}
            rows={nextDates.map(k => [
              k.label || k.key,
              <span key='d' style={{ color: k.overdue ? C.red : C.body, fontWeight: k.overdue ? 700 : 400 }}>
                {date(k.date)}{k.overdue ? ' · overdue' : ''}
              </span>,
            ])}
          />
        )}
      </Panel>

      {/* ── Recent activity ──
          A short tail of what has happened on this renewal, so the Overview
          says who last touched it without sending the reader to another tab. */}
      <Panel title='Recent activity'>
        <ActivityTab resource='renewals' id={r._id} limit={5} compact />
      </Panel>

      {/* ── The record itself ──
          Correcting, moving and withdrawing a record, in one place on the
          Overview tab. Before this a screen could create a record and then
          never touch it again. Removal is a void or a cancel, never a
          delete — see reinsuranceShared.js. */}
      <Panel title='Record'>
        <div style={{ display: 'grid', gap: 10 }}>
          <EditPanel
            record={r}
            endpoint='/reinsurance/renewals'
            onSaved={reload}
            fields={[
            { key: 'cedantName', label: 'Cedant' },
            { key: 'classOfBusiness', label: 'Class' },
            { key: 'renewalDate', label: 'Renewal date', type: 'date' },
            { key: 'targetStrategy', label: 'Target strategy' },
            { key: 'handlerName', label: 'Handler' },
            ]}
          />
          <StatusAction
            record={r}
            endpoint='/reinsurance/renewals'
            onSaved={reload}
            options={[{ value: 'on_track', label: 'On Track' }, { value: 'due_soon', label: 'Due Soon' }, { value: 'at_risk', label: 'At Risk' }, { value: 'overdue', label: 'Overdue' }, { value: 'renewed', label: 'Renewed' }, { value: 'lapsed', label: 'Lapsed' }, { value: 'declined', label: 'Declined' }]}
          />
          <RemoveAction record={r} endpoint='/reinsurance/renewals' noun='renewal plan' onRemoved={reload} />
        </div>
      </Panel>

      <Panel title='Actions'>
        <ActionRow>
          <ConfirmAction
            label='Complete renewal'
            question={`Mark ${r.renewalNumber} as renewed? This closes the plan and records the outcome.`}
            confirmLabel='Mark renewed'
            busy={act.saving}
            onConfirm={() => act.run({
              url: `/reinsurance/renewals/${r._id}/complete`,
              body: { outcome: 'renewed' },
              success: 'Renewal completed.',
            })}
          />
          <ActionButton
            tone='quiet'
            busy={act.saving}
            onClick={() => act.run({
              method: 'patch',
              url: `/reinsurance/renewals/${r._id}`,
              body: { status: 'lapsed' },
              success: 'Marked lapsed.',
            })}
          >
            Mark lapsed
          </ActionButton>
        </ActionRow>
      </Panel>

      {r.nextAction && (
        <NextAction
          label={r.nextAction.label || r.nextAction}
          detail={r.nextAction.detail}
        />
      )}
    </>
  );
}
